(function () {
    "use strict";

    // ── CONFIG ──────────────────────────────────────────────────────────────────
    const CHECKOUT_PLATFORM_URL = "https://umed-checkout-production.up.railway.app/checkout";

    // Currencies that should be routed through the USD checkout platform.
    // Add any currency you accept via Razorpay international that isn't INR.
    const NON_INR_CURRENCIES = ["USD", "GBP", "EUR", "CAD", "AUD", "SGD", "AED", "SAR"];

    // ── DETECT ACTIVE CURRENCY ──────────────────────────────────────────────────
    /**
     * Checks multiple sources for the active currency, in priority order.
     * Returns { currency: "USD", source: "window.Shopify.currency.active" } (or similar).
     *
     * Many third-party currency converter apps (e.g. MLVEDA, Auto Currency Switcher)
     * update their own globals/DOM without updating window.Shopify.currency.active,
     * so we fall through a chain of known sources.
     */
    function detectCurrency() {
      const visibleEl = document.querySelector('.bacurr-choiceDesign');
      if (visibleEl) {
        const text = (visibleEl.textContent || "").trim().toUpperCase();
        if (text.length === 3) return { currency: text, source: ".bacurr-choiceDesign" };
      }
      return { currency: "INR", source: "default fallback" };
    }

    function getActiveCurrency() {
      return detectCurrency().currency;
    }

    function isNonINR() {
      const { currency, source } = detectCurrency();
      const nonINR = NON_INR_CURRENCIES.includes(currency);
      console.log(
        `[UmedMart] Currency detected: ${currency} (source: ${source}) → ${nonINR ? "routing to USD checkout" : "using standard INR checkout"}`
      );
      return nonINR;
    }

    // ── FETCH CART ──────────────────────────────────────────────────────────────
    /**
     * Uses Shopify's Ajax Cart API (/cart.js) — no authentication required.
     * Returns a promise resolving to an array of cart line items.
     *
     * Each item shape:
     *   { title, quantity, price (in store currency cents), variant_id, ... }
     */
    async function fetchCart() {
      const res = await fetch("/cart.js", {
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch cart");
      return res.json();
    }

    // ── BUILD CHECKOUT URL ──────────────────────────────────────────────────────
    /**
     * Encodes the cart as a URL parameter so the checkout page can read it
     * without a server round-trip.
     *
     * Prices are passed as raw INR values (paise ÷ 100). The server converts
     * them to the selected currency using live exchange rates.
     */
    function buildCheckoutURL(cart) {
      const currency = getActiveCurrency();

      const items = cart.items.map((item) => ({
        title:     item.title,
        quantity:  item.quantity,
        priceINR:  (item.price / 100).toFixed(2), // Shopify price is in INR paise
        variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
        grams:     item.grams,
      }));

      const payload = { currency, items };
      const encoded = encodeURIComponent(btoa(JSON.stringify(payload)));
      return `${CHECKOUT_PLATFORM_URL}?cart=${encoded}`;
    }

    // ── INTERCEPT CHECKOUT CLICKS ────────────────────────────────────────────────

    function isCheckoutTarget(el) {
      // Positive match: must satisfy at least one of these specific conditions
      const isMatch = (
        (el.name === "checkout" && el.type === "submit") ||
        el.classList.contains("checkout-page-cart") ||
        el.id === "checkout" ||
        el.hasAttribute("data-checkout-button")
      );
      if (!isMatch) return false;

      // Negative guards: exclude known non-checkout triggers
      if (el.name === "add") return false;
      const cls = el.className || "";
      if (/add[-_]?to[-_]?cart/i.test(cls) || /product[-_]?form__submit/i.test(cls)) return false;

      // Exclude buttons inside an add-to-cart form
      const form = el.closest("form") ||
        (el.getAttribute("form") ? document.getElementById(el.getAttribute("form")) : null);
      if (form) {
        const action = (form.action || "").replace(window.location.origin, "");
        if (action.includes("/cart/add")) return false;
      }

      return true;
    }

    document.addEventListener(
      "click",
      async (event) => {
        const target = event.target.closest("button, a, input[type='submit']");
        if (!target) return;
        if (!isCheckoutTarget(target)) return;

        console.log("[UmedMart] Checkout click intercepted:", target.name || target.id || target.className);

        if (!isNonINR()) return; // let INR customers through to native checkout

        event.preventDefault();
        event.stopImmediatePropagation();

        const originalText = target.innerText || target.value || "Checkout";
        target.disabled = true;
        target.innerText = "Redirecting…";

        try {
          const cart = await fetchCart();

          if (!cart.items || cart.items.length === 0) {
            alert("Your cart is empty. Please add items before checking out.");
            target.disabled = false;
            target.innerText = originalText;
            return;
          }

          window.location.href = buildCheckoutURL(cart);

        } catch (err) {
          console.error("[UmedMart USD Checkout] Interception error:", err);
          // Fall through to native checkout on any error
          target.disabled = false;
          target.innerText = originalText;
          target.click();
        }
      },
      true // capture phase — fires before Shopify's own handlers
    );

    // ── OPTIONAL: Badge next to checkout button ─────────────────────────────────
    /**
     * Adds a small "Pay in USD" label near checkout buttons when USD is active,
     * so customers understand they'll be charged in USD.
     * Remove this block if you don't want the visual indicator.
     */
    function addUSDBadge() {
      if (!isNonINR()) return;

      const style = document.createElement("style");
      style.textContent = `
        .umed-usd-badge {
          display: inline-block;
          background: #111;
          color: #fff;
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          margin-left: 8px;
          vertical-align: middle;
          font-family: sans-serif;
          letter-spacing: 0.04em;
        }
      `;
      document.head.appendChild(style);

      // Wait for the DOM to be ready before inserting badges
      const insertBadges = () => {
        document.querySelectorAll('[name="checkout"], .cart__checkout-button').forEach((btn) => {
          if (!btn.querySelector(".umed-usd-badge")) {
            const badge = document.createElement("span");
            badge.className = "umed-usd-badge";
            badge.textContent = `Pay in ${getActiveCurrency()}`;
            btn.appendChild(badge);
          }
        });
      };

      // Run on load and after any DOM changes
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", insertBadges);
      } else {
        insertBadges();
      }

      // Watch for dynamically rendered cart drawers / mini-carts
      const observer = new MutationObserver(insertBadges);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    addUSDBadge();

  })();
